import {
  CardListItemButton,
  ErrorListItem,
  ListContentIsland,
  ListItem,
  NoCardsListItem,
  SkeletonListItem,
  TextField,
  Collapse,
} from '@razz-team/ui-web';
import { useHasRole } from '../../../api/access';
import { ApiCard } from '../../../openapi';
import { ReactNode, useDeferredValue, useMemo, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { useTranslation } from 'react-i18next';

export function CardList({
  title,
  actions,
  cards,
  isLoading,
  error,
  isSearchEnabled = false,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  cards: ApiCard[] | undefined;
  error: Error | null | undefined;
  isLoading: boolean;
  isSearchEnabled?: boolean;
}) {
  const { t } = useTranslation();
  const isViewer = useHasRole('VIEWER');

  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  const filteredCards = useMemo(() => {
    const q = deferredQuery.trim().toLocaleLowerCase();
    if (!cards || q === '') return cards ?? [];
    return cards.filter((card) => {
      if (card.id === q) return true;
      if (card.name?.toLocaleLowerCase().includes(q)) return true;
      if (card.cardHolderName?.toLocaleLowerCase().includes(q)) return true;
      if (card.lastFour?.includes(q)) return true;
      return false;
    });
  }, [cards, deferredQuery]);

  const hasResults = (filteredCards?.length ?? 0) > 0;

  return (
    <ListContentIsland title={title} actions={actions}>
      {isSearchEnabled && (
        <ListItem>
          <TextField
            type="search"
            label={t('Search')}
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            fullWidth
          />
        </ListItem>
      )}
      {isLoading ? (
        <>
          <SkeletonListItem variant="withButton" />
          <SkeletonListItem variant="withButton" />
          <SkeletonListItem variant="withButton" />
        </>
      ) : error ? (
        <ErrorListItem error={error} />
      ) : hasResults ? (
        <TransitionGroup>
          {(isSearchEnabled ? filteredCards : (cards ?? [])).map((card) => (
            <Collapse key={card.id} in timeout={100}>
              <ListItem disablePadding>
                <CardListItemButton<'a'>
                  card={card}
                  href={`/account/cards/${card.id}`}
                  withHolderName={isViewer}
                />
              </ListItem>
            </Collapse>
          ))}
        </TransitionGroup>
      ) : (
        <NoCardsListItem />
      )}
    </ListContentIsland>
  );
}
